/**
 * Theme.
 *
 * The `<html>` class is the source of truth and it is set by an inline script in
 * `index.html` before the first paint. This provider only mirrors that value into
 * React so a toggle can render the correct icon, and writes back when the user
 * changes it.
 *
 * ## Why the DOM is the store
 *
 * Because the alternative is a flash of the wrong theme. If React owned the
 * value, the first render would necessarily happen before any effect could apply
 * the class, and a dark-preferring user would see one frame of the light palette.
 * That one frame is the single most-complained-about defect in dark-mode
 * implementations, and it is entirely avoidable: a synchronous script in `<head>`
 * runs before the body is parsed, so the class is already correct when the first
 * pixel is painted.
 *
 * ## Why three states and not two
 *
 * `system` is a real preference and it is the default. A user who has never
 * touched the toggle should follow their operating system as it changes - a
 * laptop that switches to dark at sunset should switch the board too. Storing
 * only `light`/`dark` would freeze the value at whatever the system happened to
 * be on the first visit.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'slate.theme';

export interface ThemeContextValue {
  /** What the user chose. */
  preference: ThemePreference;
  /** What is actually rendered, after resolving `system`. */
  resolved: 'light' | 'dark';
  setPreference(next: ThemePreference): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // `localStorage` throws in some private-browsing modes and when storage is
    // full. Following the system preference is the correct fallback.
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyClass(dark: boolean): void {
  document.documentElement.classList.toggle('dark', dark);
}

export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  const resolved: 'light' | 'dark' = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  // Track the system preference so a `system` user follows it live.
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // The single place the DOM is written. Runs on every resolved change, which
  // includes the first mount - harmless, because the inline script has already
  // set the same class.
  useEffect(() => {
    applyClass(resolved === 'dark');
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      // `system` is stored as absence rather than as the string "system", so
      // that the inline script's `stored ? stored === 'dark' : prefersDark`
      // branch resolves correctly without it having to know about the third
      // value. The script stays three lines and cannot drift from this module.
      if (next === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable. The preference still applies for this session.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) throw new Error('useTheme must be used inside <ThemeProvider>.');
  return value;
}
